// src/components/register/StyledDropdown.tsx

import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@radix-ui/react-dropdown-menu';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { Button } from '../../styles/_app.style';
import { Item } from '../../styles/navigation.style';
import { Content } from '../navigation/navDropdowns/navDropdown.style';

// Define the shape of the structured options
type Option = { value: string; label: string };
export type OptionGroup = { label: string; options: Option[] };

// Define the component's props
interface StyledDropdownProps {
  value: string;
  onChange: (val: string) => void;
  options: OptionGroup[];
  placeholder?: string;
}

const StyledDropdown: React.FC<StyledDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
}) => {
  const [open, setOpen] = useState(false);

  // Find the display label for the currently selected value
  const displayLabel = 
    options.flatMap(group => group.options).find(opt => opt.value === value)?.label || placeholder;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          icon
          type="button" 
          css={{
            width: '100%', 
            
            // 1. FLEX LAYOUT
            display: 'flex',
            justifyContent: 'space-between', 
            alignItems: 'center',            
            textAlign: 'left',               
            
            // 2. TEXT WRAPPING & TIGHT PADDING
            whiteSpace: 'normal',            
            height: 'auto',                  
            minHeight: '2.75rem',            
            
            // 👇 THE FIX: Tighten side padding to align with labels
            padding: '0.5rem 0.3rem',       
            
            lineHeight: '1.3',               

            gap: '0.5rem',
            fontSize: '$sm',

            '&:hover, &:active, &[data-state="open"]': {
              backgroundColor: '$elementBackgroundHover', 
            },
          }}
          aria-label={placeholder}
        >
          {/* Wrap text in a span that grows */}
          <span style={{ flex: 1, wordBreak: 'break-word' }}>
            {displayLabel}
          </span>

          {/* Icon stays fixed size and won't shrink */}
          <span style={{ display: 'flex', flexShrink: 0 }}>
             {open ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sideOffset={4}
        style={{
            maxHeight: '15rem',
            overflowY: 'auto',
            zIndex: 999,
            fontSize: 'var(--fontSizes-sm)', 
        }}
        asChild
      >
        <Content 
          style={{ 
            minWidth: 'var(--radix-dropdown-menu-trigger-width)',
            maxWidth: 'var(--radix-dropdown-menu-trigger-width)', 
          }}
        >
          {options.map(group => (
            <DropdownMenuGroup key={group.label}>
              <DropdownMenuLabel style={{ padding: '0.5rem 0.5rem', fontWeight: 'bold', fontSize: '0.85em', color: '#666' }}>
                {group.label}
              </DropdownMenuLabel>
              
              {group.options.map(opt => (
                <Item
                  key={opt.value}
                  onSelect={() => onChange(opt.value)}
                  style={{ 
                    width: '100%',
                    whiteSpace: 'normal',    
                    height: 'auto',          
                    wordBreak: 'break-word', 
                    padding: '0.5rem 0.5rem', // Consistent padding inside the menu
                  }}
                  title={opt.label}
                >
                  {opt.label}
                </Item>
              ))}
            </DropdownMenuGroup>
          ))}
        </Content>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StyledDropdown;